import * as CommonArrayUtils from '@sparta.global.utilities/common-array-utils-utility/dist/es6.js';
import * as CommonStringUtils from '@sparta.global.utilities/common-string-utils-utility/dist/es6.js';

/**
 * @name        DOM Utility
 * @exports     DomUtility
 * @example
 *  const { findEl } = DomUtility;
 *
 *  class MyModule extends Module {
 *    init() {
 *      const button = findEl('.button', this.settings.target);
 *      // do something with button
 *    }
 *  }
 * @file        dom-utility.js
 * @description DOM utility for DOM manipulation.
 * @classdesc   DOM utility for DOM manipulation.
 * @global      Creates a global DOMUtility object
 * @author      Brandon Minton , Sunny Singh
 * @copyright   © 2018 Bank of America Corporation. All rights reserved.
 */

const {
  isValueInArray
} = CommonArrayUtils;
const {
  isValueInString,
  stringHasAllValues
} = CommonStringUtils;

/**
 * Finds a single element.
 * Also availabe in scoped element object from `createElScope`.
 * @mockElement span#blah.test[aria-hidden="true"] some content
 * @testExample findEl('#blah')
 * @testOutput equal #blah
 * @testException findEl('#blah', 'blah')
 * @param {String} selector Selector.
 * @param {Object} [scope=document] DOM Node to scope selector to.
 * @returns {Object} DOM Node.
 */
function findEl(selector, scope = document) {
  const scopeEl = typeof scope === 'string' ? document.querySelector(scope) : scope;
  const notValidScopeEl = typeof scopeEl !== 'object' || !scopeEl.nodeType;
  if (notValidScopeEl) throw new Error('DomUtility: A valid DOM Node must be passed as scope.');
  return scopeEl.querySelector(selector);
}

/**
 * Finds multiple elements.
 * Also availabe in scoped element object from `createElScope`.
 * @example findAllEls('li')
 * @param {String} selector Selector.
 * @param {Object} [scope=document] DOM Node to scope selector to.
 * @returns {Array} Array of DOM Nodes.
 */
function findAllEls(selector, scope = document) {
  const scopeEl = typeof scope === 'string' ? document.querySelector(scope) : scope;
  const notValidScopeEl = typeof scopeEl !== 'object' || !scopeEl.nodeType;
  if (notValidScopeEl) throw new Error('DomUtility: A valid DOM Node must be passed as scope.');
  const els = scopeEl.querySelectorAll(selector);
  return els ? [...els] : [];
}

/**
 * Ensures that an element gets returned, no matter if an element or selector is passed in.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {Object} [scope=document] DOM Node to scope selector to.
 * @returns {Object} DOM Node.
 */
function ensureEl(elOrSelector, scope = document) {
  if (typeof elOrSelector === 'string') return findEl(elOrSelector, scope);
  if (elOrSelector === null) return null;
  if (typeof elOrSelector === 'object' && elOrSelector.nodeType) return elOrSelector;
  throw new Error('DomUtility: A valid selector or DOM Node must be passed.');
}

/**
 * Ensures that elements get returned, no matter if elements or selector is passed in.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @param {Object} [scope=document] DOM Node to scope selector to.
 * @returns {Array} Array of DOM Nodes.
 */
function ensureAllEls(elOrSelector, scope = document) {
  if (typeof elOrSelector === 'string') return findAllEls(elOrSelector, scope);
  if (Array.isArray(elOrSelector)) return elOrSelector;
  if (typeof elOrSelector === 'object' && elOrSelector.constructor === NodeList) return elOrSelector;
  throw new Error('DomUtility: A valid selector or DOM NodeList must be passed.');
}

/**
 * Creates a reference of elements.
 * Also availabe in scoped element object from `createElScope`.
 * @testExample createElMap({ goToApplicationButton: '.go-to-application-button', cancelButton: '.cancel-button' }, '#myModule')
 * @example
 *  const els = createElMap({
 *    goToApplicationButton: '.go-to-application-button',
 *    cancelButton: '.cancel-button',
 *  }, '#myModule');
 *
 *  // do something with els.goToApplicationButton or els.cancelButton
 * @param {Object} selectors Selectors to use when mapping elements.
 * @param {object} [scope=document] DOM Node to scope selectors to.
 * @returns {Object} The same `selectors` object but with selectors swapped with elements.
 */
function createElMap(selectors, scope = document) {
  const map = {};
  Object.keys(selectors).forEach(key => {
    const selector = selectors[key];
    if (typeof selector !== 'string') {
      map[key] = selector;
      return;
    }
    map[key] = findEl(selector, scope);
  });
  return map;
}

/**
 * Checks if object is a NodeList or not.
 * @mockElement li
 * @example isNodeList(mockedElements[0]);
 * @param {Object} el DOM Node.
 * @returns {Boolean} Is NodeList.
 */
function isNodeList(el) {
  return !!(el && el.constructor === NodeList);
}

/**
 * Finds element whose innerText matches the given text.
 * Also availabe in scoped element object from `createElScope`.
 * TODO: Deal with innerText behavior where children text is matched.
 * @mockElement #test Find me
 * @example findElByText('#test', 'Find me', foundEl => {  })
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} text Text to find.
 * @param {Function} callback Callback function when text is found. May be fired multiple times.
 * @returns {void}
 */
function findElByText(elOrSelector, text, callback) {
  const el = ensureEl(elOrSelector);
  if (el.innerText.match(text)) {
    callback(el);
  } else {
    [...el.children].forEach(foundEl => findElByText(foundEl, text, callback));
  }
}

/**
 * Ponyfill for Element.closest()
 * Also availabe in scoped element object from `createElScope`.
 * Not a typo. See: https://ponyfoo.com/articles/polyfills-or-ponyfills
 * More info: https://developer.mozilla.org/en-US/docs/Web/API/Element/matches
 *
 * @mockElement ul#parent <li id="test">test</li>
 * @testExample closestEl('#test', '#parent');
 * @testOutput exist
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} selector Selector string.
 * @returns {Object} DOM Node.
 */
function closestEl(elOrSelector, selector) {
  let el = ensureEl(elOrSelector);
  const matches = () => {
    if (Element.prototype.matches) return el.matches(selector);
    if (Element.prototype.msMatchesSelector) {
      return Element.prototype.msMatchesSelector.call(el, selector);
    }
    return Element.prototype.webkitMatchesSelector.call(el, selector);
  };
  if (!document.documentElement.contains(el)) return null;
  do {
    if (matches()) return el;
    el = el.parentElement || el.parentNode; // eslint-disable-line no-param-reassign
  } while (el !== null && el.nodeType === 1);
  return null;
}

/**
 * Instantiates a Mutation Observer and observes an element for changes.
 * Also availabe in scoped element object from `createElScope`.
 * More info:  https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver#MutationObserverInit
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {Object} options Observer options.
 * @param {Function} callback Callback function.
 * @returns {void}
 */
function observeEl(elOrSelector, options, callback) {
  const el = ensureEl(elOrSelector);
  const mutationCallback = allmutations => {
    allmutations.map(mutationRecord => callback(mutationRecord));
  };
  const Observer = new MutationObserver(mutationCallback);
  Observer.observe(el, options);
}

/**
 * Instantiates a Mutation Observer and observes an element for specific class changes.
 * @param {Function} callbackIf
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} classes
 * @returns {boolean}
 */
function whenElHasClasses(elOrSelector, classes = [], callback) {
  const el = ensureEl(elOrSelector);
  if (classes.constructor !== Array) ;
  observeEl(el, {
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ['class']
  }, change => {
    const hasAllValues = !stringHasAllValues(change.oldValue, ...classes);
    callback.call(null, hasAllValues);
  });
}

/**
 * Finds the position of your element. Function stolen from Kirupa.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @returns {Object} X and Y coordinates of that element.
 */
function getElPosition(elOrSelector) {
  const el = ensureEl(elOrSelector);
  let xPos = 0;
  let yPos = 0;
  let currentEl = el;
  while (currentEl) {
    if (currentEl.tagName === 'BODY') {
      // deal with browser quirks with body/window/document and page scroll
      const xScroll = currentEl.scrollLeft || document.documentElement.scrollLeft;
      const yScroll = currentEl.scrollTop || document.documentElement.scrollTop;
      xPos += currentEl.offsetLeft - xScroll + currentEl.clientLeft;
      yPos += currentEl.offsetTop - yScroll + currentEl.clientTop;
    } else {
      xPos += currentEl.offsetLeft - currentEl.scrollLeft + currentEl.clientLeft;
      yPos += currentEl.offsetTop - currentEl.scrollTop + currentEl.clientTop;
    }
    currentEl = currentEl.offsetParent;
  }
  return {
    x: xPos,
    y: yPos
  };
}

/**
 * Changes data into a string and adds it to the element's data.
 * Also availabe in scoped element object from `createElScope`.
 * TODO: Make sure return value is consistent. Is this needed or do we need `setElData()` too?
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} key Data key.
 * @param {Object} [elData] Data object.
 * @returns {Object|String} Parsed data or just the string if it's not JSON.
 */
function getElData(elOrSelector, key, elData) {
  const el = ensureEl(elOrSelector);
  if (elData) {
    el.dataset[key] = JSON.stringify(elData); // eslint-disable-line no-param-reassign
    return el;
  }
  try {
    return JSON.parse(el.dataset[key]);
  } catch (error) {
    return el.dataset[key]; // data string
  }
}

/**
 * Checks if an element is visible on the page.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @returns {Boolean} Is visible.
 */
function isElVisible(elOrSelector) {
  const el = ensureEl(elOrSelector);
  return el.offsetParent !== null;
}

/**
 * Checks if an element is hidden on the page.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @returns {Boolean} Is hidden.
 */
function isElHidden(elOrSelector) {
  const el = ensureEl(elOrSelector);
  return el.offsetParent === null;
}

/**
 * Filters elements by data-* attribute key.
 * Also availabe in scoped element object from `createElScope`.
 * @example
 *   filterByDataAttrKey('li', 'item')
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @param {String} attrKey Attribute key.
 * @returns {Array} Filtered array of elements.
 */
function filterByDataAttrKey(elOrSelector, attrKey) {
  const els = ensureAllEls(elOrSelector);
  return [...els].filter(el => isValueInArray(attrKey, [...Object.keys(el.dataset)]));
}

/**
 * Filters elements by attribute value.
 * Also availabe in scoped element object from `createElScope`.
 * @mockElement ul#test <li class="test" data-item="1"></li><li class="test" data-item="2"></li>
 * @example filterByDataAttrValue('li.test', 'item', '1')
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @param {String} attrKey Attribute key.
 * @param {String} attrValue Attribute value.
 * @returns {Array} Filtered array of elements.
 */
function filterByDataAttrValue(elOrSelector, attrKey, attrValue) {
  const els = ensureAllEls(elOrSelector);
  return [...els].filter(el => isValueInString(attrValue, el.dataset[attrKey] || ''));
}

/**
 * Filters elements by attribute key and value.
 * Also availabe in scoped element object from `createElScope`.
 * TODO: Make this work.
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @param {String} attrKey Attribute key.
 * @param {String} attrValue Attribute value.
 * @returns {Array} Filtered array of elements.
 */
function filterByDataAttr(elOrSelector, attrKey, attrValue = '') {
  ensureAllEls(elOrSelector);
  if (isValueInString('=', attrKey)) ;
}

/**
 * Filters elements by class name.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @param {String} className Class name.
 * @returns {Array} Filtered elements.
 */
function filterByClass(elOrSelector, className) {
  const els = ensureAllEls(elOrSelector);
  return [...els].filter(el => isValueInArray(className, [...el.classList]));
}

/**
 * Filters elements if they're visible.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @returns {Array} Filtered elements.
 */
function filterIfVisible(elOrSelector) {
  const els = ensureAllEls(elOrSelector);
  return [...els].filter(el => isElVisible(el));
}

/**
 * Filters elements if they're hidden.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @returns {Array} Filtered elements.
 */
function filterIfHidden(elOrSelector) {
  const els = ensureAllEls(elOrSelector);
  return [...els].filter(el => isElHidden(el));
}

/**
 * Filters elements by selector.
 * Also availabe in scoped element object from `createElScope`.
 * TODO: Make this work.
 * @param {Object|String} elOrSelector DOM NodeList or selector.
 * @param {String} str Selector string.
 * @returns {Object} Filtered elements.
 */
function filterAllEls(elOrSelector, str) {
  const el = ensureEl(elOrSelector);

  // TODO: Replace with css-selector-parser-utility
  /* eslint-disable */
  const selectorObj = {
    el,
    str,
    id: (el, id) => {
      // TODO
    },
    attrs: (el, str) => {
      // TODO
    },
    classes: (elements, str) => {
      filterByClass(elements);
      // TODO
    }
  };
  /* eslint-enable */

  return parseDOMString(str, selectorObj);
}

/**
 * Removes an element.
 * Also availabe in scoped element object from `createElScope`.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @returns {Object} Removed element.
 */
function removeEl(elOrSelector) {
  const el = ensureEl(elOrSelector);
  if (typeof el.remove === 'function') {
    el.remove();
    return el;
  }
  if (el.parentNode) el.parentNode.removeChild(el); // IE

  return el;
}

/**
 * Removes an element's children.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @returns {Object} element who has aborted all of its children.
 */
function removeChildrenEls(elOrSelector) {
  const el = ensureEl(elOrSelector);
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
  return el;
}

/**
 * Creates markup from string.
 * @param {*} markupString Markup string.
 * @returns {String} Parsed markup string.
 */
function createMarkup(markupString) {
  const parser = new DOMParser();
  const markup = parser.parseFromString(markupString, 'text/html').body.childNodes[0];
  return markup;
}

/**
 * Creates SVG DOM Node from a string of markup.
 * @param {String} markupString Markup string.
 * @returns {Object} DOM Node.
 */
function createSvg(markupString) {
  const ns = 'http://www.w3.org/2000/svg';
  const parser = new DOMParser();
  const parsedCart = parser.parseFromString(markupString, 'text/xml');
  const svg = document.createElementNS(ns, 'svg');
  svg.appendChild(document.importNode(parsedCart.documentElement, true));
  return svg.childNodes[0];
}

/**
 * Shows DOM element.
 * Also availabe in scoped element object from `createElScope`.
 * @mockElement span#blah.test[aria-hidden="true"] some content
 * @testExample showEl('#blah')
 * @testOutput deepEqual #blah
 * @testExpect mockedElements[0].classList.contains('hide') toBeFalse
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String} [hiddenClassName='hide'] Class name.
 * @returns {Object} DOM Node.
 */
function showEl(elOrSelector, hiddenClassName = 'hide') {
  const el = ensureEl(elOrSelector);
  el.classList.remove(hiddenClassName);
  return el;
}

/**
 * Hides DOM element.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String} [hiddenClassName='hide'] Class name.
 * @returns {Object} DOM Node.
 */
function hideEl(elOrSelector, hiddenClassName = 'hide') {
  const el = ensureEl(elOrSelector);
  el.classList.add(hiddenClassName);
  return el;
}

/**
 * Toggles a single DOM element.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String} [hiddenClassName='hide'] Class name.
 * @returns {Object} DOM Node.
 */
function toggleEl(elOrSelector, hiddenClassName = 'hide') {
  const el = ensureEl(elOrSelector);
  if (el.classList.contains(hiddenClassName)) {
    // If the element is visible, hide it
    showEl(el, hiddenClassName);
  } else {
    hideEl(el, hiddenClassName);
  }
  return el;
}

/**
 * Toggles multiple DOM elements.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @returns {void}
 */
function toggleEls(...elOrSelector) {
  toggleEl(elOrSelector);
}

/**
 * Appends an element to a parent.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String|Object} parentOrSelector DOM NodeList or selector.
 * @returns {Object} DOM Node.
 */
function appendEl(elOrSelector, parentOrSelector) {
  const el = ensureEl(elOrSelector);
  const parent = ensureEl(parentOrSelector);
  parent.appendChild(el);
  return el;
}

/**
 * Inserts an element before a sibling.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String|Object} siblingOrSelector DOM NodeList or selector.
 * @returns {Object} DOM Node.
 */
function insertElBefore(elOrSelector, siblingOrSelector) {
  const el = ensureEl(elOrSelector);
  const sibling = ensureEl(siblingOrSelector);
  sibling.parentNode.insertBefore(el, sibling);
  return el;
}

/**
 * Inserts an element after a sibling.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String|Object} siblingOrSelector DOM NodeList or selector.
 * @returns {Object} DOM Node.
 */
function insertElAfter(elOrSelector, siblingOrSelector) {
  const el = ensureEl(elOrSelector);
  const sibling = ensureEl(siblingOrSelector);
  const nextSibling = sibling && sibling.nextSibling;
  const parent = sibling.parentNode;
  if (nextSibling) {
    parent.insertBefore(el, nextSibling);
  } else if (!nextSibling) {
    // sibling is the last child el of parent
    parent.appendChild(el);
  }
  return el;
}

/**
 * Wraps an element within a parent.
 * Also availabe in scoped element object from `createElScope`.
 * @param {String|Object} elOrSelector DOM NodeList or selector.
 * @param {String|Object} parentOrSelector DOM NodeList or selector.
 * @returns {Object} DOM Node.
 */
function wrapEl(elOrSelector, parentOrSelector) {
  const el = ensureEl(elOrSelector);
  const parentEl = ensureEl(parentOrSelector);
  insertElBefore(parentEl, el);
  appendEl(el, parentEl);
  return el;
}

/**
 * Delegates event listeners to a parent element.
 * Also availabe in scoped element object from `createElScope`.
 * Learn about event delegation: https://davidwalsh.name/event-delegate
 *
 * @example
 *  delegateEvent('click', 'li', 'ul', (event) => {
 *    console.log('Clicked button: ', event.target);
 *  });
 * @param {String} event Event to listen for.
 * @param {String} selector Selector to delegate to target.
 * @param {String|Object} parentOrSelector Parent DOM NodeList or selector to delegate to target.
 * @param {Function} handler Handler for event being listened for.
 * @param {Object} options Options object for once and useCapture options. Defaults are false.
 * @returns {void}
 */
function delegateEvent(event, selector, parentOrSelector, handler, options = {
  once: false,
  useCapture: false
}) {
  const parentEl = ensureEl(parentOrSelector);
  parentEl.addEventListener(event, delegatedEvent => {
    for (let {
      target
    } = delegatedEvent; target && target !== this; target = target.parentNode) {
      if (closestEl(target, selector)) {
        if (options.once) {
          if (delegatedEvent.currentTarget.dataset.triggered) return;
          // eslint-disable-next-line no-param-reassign
          delegatedEvent.currentTarget.dataset.triggered = true;
        }
        handler.call(target, delegatedEvent);
        break;
      }
    }
  }, options.useCapture);
}

/**
 * Like the above ponyfill doUntilClosestEl, except with a callback per parent element.
 * Also availabe in scoped element object from `createElScope`.
 *
 * @mockElement ul#parent <li id="test">test</li>
 * @testExample doUntilClosestEl('#test', '#parent', (el) => { el.textContent = 'updated'; });
 * @testOutput exist
 * @testExpect mockedElements[0].querySelector('#test').textContent toEqual 'updated'
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} selector Selector string.
 * @param {Function} callback runs on each parent node
 * @returns {Object} DOM Node.
 */
function doUntilClosestEl(elOrSelector, selector, callback) {
  let el = ensureEl(elOrSelector);
  const matches = () => {
    if (Element.prototype.matches) return el.matches(selector);
    if (Element.prototype.msMatchesSelector) {
      return Element.prototype.msMatchesSelector.call(el, selector);
    }
    return Element.prototype.webkitMatchesSelector.call(el, selector);
  };
  if (!document.documentElement.contains(el)) return null;
  do {
    if (matches()) return el;
    callback(el);
    el = el.parentElement || el.parentNode; // eslint-disable-line no-param-reassign
  } while (el !== null && el.nodeType === 1);
  return null;
}

/**
 * Given an element, return a list of z-indices.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} untilSelector Selector string where we will stop our node traversal.
 * @returns {Array} an array of z indices of the parent nodes.
 */
function getZIndices(elOrSelector, untilSelector = 'body') {
  const el = ensureEl(elOrSelector);
  var zIndices = [];
  doUntilClosestEl(el, untilSelector, outerEl => {
    const zIndex = getComputedStyle(outerEl).zIndex;
    if (zIndex && zIndex !== 'auto') zIndices.push(parseInt(zIndex));
  });
  return zIndices;
}

/**
 * Given a list of elements, return the element sitting within the highest z-indexed parent.
 * @param {Object|String} elOrSelector DOM Node or selector.
 * @param {String} untilSelector Selector string where we will stop our node traversal.
 * @returns {Array} an array of z indices of the parent nodes.
 */
function getHighestEl(elOrSelector, untilSelector = 'body') {
  const els = ensureAllEls(elOrSelector);
  const elZIndices = els.map(el => {
    const zIndices = getZIndices(el, untilSelector);
    const maxIndex = zIndices.length && Math.max(...zIndices) || 0;
    return maxIndex;
  });
  const arrIndex = elZIndices.indexOf(Math.max(...elZIndices));
  return els[arrIndex];
}

/**
 * Create scoped DomUtility functions.
 * @testExample createElScope({});
 * @example
 *  const { createElScope } = DomUtility;
 *
 *  class MyModule extends Module {
 *    init() {
 *      const module = createElScope(this.settings.target);
 *      const button = module.findEl('.button');
 *      // do something with button
 *    }
 *  }
 * @param {String|Object} elOrSelector DOM NodeList or selector to use as scope.
 * @returns {Object} Scoped DomUtility functions.
 */
function createElScope(elOrSelector) {
  const scope = ensureEl(elOrSelector);
  return {
    findEl: el => findEl(el, scope),
    findAllEls: els => findAllEls(els, scope),
    ensureEl: el => ensureEl(el, scope),
    ensureAllEls: els => ensureAllEls(els, scope),
    createElMap: selectors => createElMap(selectors, scope),
    findElByText: (el, text, callback) => findElByText(findEl(el, scope), text, callback),
    closestEl: (el, selector) => closestEl(findEl(el, scope), selector),
    observeEl: (el, options, callback) => observeEl(findEl(el, scope), options, callback),
    getElPosition: el => getElPosition(findEl(el, scope)),
    getElData: (el, key, elData) => getElData(findEl(el, scope), key, elData),
    isElVisible: el => isElVisible(findEl(el, scope)),
    isElHidden: el => isElHidden(findEl(el, scope)),
    filterByDataAttrKey: (el, attrKey) => filterByDataAttrKey(findEl(el, scope), attrKey),
    filterByDataAttrValue: (el, attrKey, attrValue) => filterByDataAttrValue(findEl(el, scope), attrKey, attrValue),
    filterByDataAttr: (el, attrKey, attrValue) => filterByDataAttr(findEl(el, scope), attrKey, attrValue),
    filterByClass: (el, className) => filterByClass(findEl(el, scope), className),
    filterIfVisible: el => filterIfVisible(findEl(el, scope)),
    filterIfHidden: el => filterIfHidden(findEl(el, scope)),
    filterAllEls: (el, str) => filterAllEls(findEl(el, scope), str),
    removeEl: el => removeEl(findEl(el, scope)),
    showEl: (el, hiddenClassName) => showEl(findEl(el, scope), hiddenClassName),
    hideEl: (el, hiddenClassName) => hideEl(findEl(el, scope), hiddenClassName),
    toggleEl: (el, hiddenClassName) => toggleEl(findEl(el, scope), hiddenClassName),
    toggleEls: els => toggleEls(...findAllEls(els, scope)),
    appendEl: (el, parent) => appendEl(findEl(el, scope), parent),
    insertElBefore: (el, siblingOrSelector) => insertElBefore(findEl(el, scope), siblingOrSelector),
    insertElAfter: (el, siblingOrSelector) => insertElAfter(findEl(el, scope), siblingOrSelector),
    wrapEl: (el, parent) => wrapEl(findEl(el, scope), parent),
    delegateEvent: (event, selector, el, handler, opts) => delegateEvent(event, selector, findEl(el, scope), handler, opts)
  };
}

export { appendEl, closestEl, createElMap, createElScope, createMarkup, createSvg, delegateEvent, doUntilClosestEl, ensureAllEls, ensureEl, filterAllEls, filterByClass, filterByDataAttr, filterByDataAttrKey, filterByDataAttrValue, filterIfHidden, filterIfVisible, findAllEls, findEl, findElByText, getElData, getElPosition, getHighestEl, getZIndices, hideEl, insertElAfter, insertElBefore, isElHidden, isElVisible, isNodeList, observeEl, removeChildrenEls, removeEl, showEl, toggleEl, toggleEls, whenElHasClasses, wrapEl };
